import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons"
import * as yup from "yup"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { useSubmitEventQrFeedback } from "../../hooks/useEventQr"

/*
  The report page's feedback questions, copied rather than shared: the client
  will send event-specific questions, and changing these must never change the
  patient form.
*/
const feedbackSchema = yup.object().shape({
  comments: yup
    .string()
    .max(500, "Comments must be 500 characters or less")
    .optional(),
  illustrations_helpful: yup
    .mixed()
    .nullable()
    .oneOf([true, false, null], "Please select a valid option")
    .test(
      "is-answered",
      "Please select an option",
      (value) => value !== undefined
    ),
  recommend_report: yup
    .mixed()
    .oneOf([true, false], "Please select a valid option")
    .required("Please select an option"),
  report_delivery: yup
    .string()
    .max(50, "Must be 50 characters or less")
    .required("Please select how you received your report"),
  spe_helpful: yup
    .mixed()
    .nullable()
    .oneOf([true, false, null], "Please select a valid option")
    .test(
      "is-answered",
      "Please select an option",
      (value) => value !== undefined
    ),
})

const questions = [
  {
    name: "illustrations_helpful",
    label: "Did the medical illustrations help you understand your diagnosis?",
    options: [
      { label: "Yes", value: true },
      { label: "No", value: false },
      { label: "N/A", value: null },
    ],
  },
  {
    name: "recommend_report",
    label: "Would you recommend this type of report to others?",
    options: [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ],
  },
  {
    name: "report_delivery",
    label: "How did you receive your report?",
    options: ["Doctor's office", "Email", "Patient portal", "Other"].map(
      (option) => ({ label: option, value: option })
    ),
  },
  {
    name: "spe_helpful",
    label: "Was the Simplified Patient Explanation helpful?",
    options: [
      { label: "Yes", value: true },
      { label: "No", value: false },
      { label: "Didn't read it", value: null },
    ],
  },
]

const ChoiceQuestion = ({ question, value, onSelect }) => (
  <div className="space-y-2.5">
    <p className="text-sm font-medium text-gray-700">{question.label}</p>
    <div className="flex flex-wrap gap-2">
      {question.options.map((option) => (
        <button
          key={option.label}
          className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
            value === option.value
              ? "bg-blue-600 text-white border-blue-600 shadow-sm"
              : "bg-white text-gray-500 border-gray-200 hover:border-blue-300 hover:text-blue-600"
          }`}
          type="button"
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
    <ErrorMessage
      className="text-red-500 text-xs"
      component="div"
      name={question.name}
    />
  </div>
)

const EventFeedbackForm = ({ publicUuid, trackingIds, isPreview }) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const { mutateAsync: submitFeedback, isPending } = useSubmitEventQrFeedback()

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-300 max-w-3xl mx-auto mt-16 mb-8">
        <div className="flex flex-col items-center justify-center space-y-4 py-8">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
            <FontAwesomeIcon className="w-10 h-10 text-white" icon={faCheck} />
          </div>
          <h3 className="text-xl font-bold text-primary">
            Thanks for your feedback!
          </h3>
          <p className="text-sm text-gray-600/80 text-center max-w-md leading-relaxed">
            We did not collect your name, email, or any identifying information.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4 py-16 px-4 max-w-5xl mx-auto text-center">
        <h6 className="uppercase tracking-widest text-xs font-bold text-blue-600">
          quick feedback
        </h6>
        <h2 className="text-3xl font-extrabold tracking-wide text-primary">
          How was your experience?
        </h2>
        <h6 className="text-sm text-gray-500 leading-relaxed">
          Takes 30 seconds - no name or email needed.
        </h6>
      </div>

      <Formik
        initialValues={{
          comments: "",
          illustrations_helpful: undefined,
          recommend_report: undefined,
          report_delivery: "",
          spe_helpful: undefined,
        }}
        validationSchema={feedbackSchema}
        onSubmit={async (values) => {
          if (isPreview || isPending) return
          setSubmitError(null)
          try {
            await submitFeedback({
              publicUuid,
              comments: values.comments || null,
              illustrationsHelpful: values.illustrations_helpful,
              recommendReport: values.recommend_report,
              reportDelivery: values.report_delivery,
              speHelpful: values.spe_helpful,
              ...trackingIds,
            })
            setIsSubmitted(true)
          } catch (error) {
            console.error("Failed to submit feedback:", error)
            setSubmitError(
              "We couldn't send your feedback. Please try again in a moment."
            )
          }
        }}
      >
        {({ values, setFieldValue, isValid, dirty }) => {
          const isSubmitDisabled = isPreview || isPending || !isValid || !dirty

          return (
            <Form className="bg-white rounded-2xl border border-gray-200 shadow-md max-w-2xl mx-auto mb-10 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">
                  Rate Your Experience
                </p>
              </div>
              <div className="px-6 py-6 space-y-5">
                {questions.map((question) => (
                  <div key={question.name} className="space-y-5">
                    <ChoiceQuestion
                      question={question}
                      value={values[question.name]}
                      onSelect={(value) => setFieldValue(question.name, value)}
                    />
                    <div className="h-px bg-gray-100" />
                  </div>
                ))}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Additional comments{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <Field
                    as="textarea"
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none h-24 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={500}
                    name="comments"
                    placeholder="Anything you'd like us to know..."
                  />
                  <div className="flex items-start justify-between">
                    <ErrorMessage
                      className="text-red-500 text-xs"
                      component="div"
                      name="comments"
                    />
                    <span className="text-xs text-gray-400 ml-auto">
                      {values.comments.length}/500
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-400">
                  We do not collect your name, email, or any identifying
                  information.
                </p>

                {submitError && (
                  <p className="text-sm text-red-600">{submitError}</p>
                )}

                <button
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-sm text-white transition-all ${
                    !isSubmitDisabled
                      ? "bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md"
                      : isPending
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-gray-300 cursor-not-allowed"
                  }`}
                  disabled={isSubmitDisabled}
                  type="submit"
                >
                  {isPreview ? (
                    "Submit Feedback (disabled in preview)"
                  ) : isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <FontAwesomeIcon
                        className="w-4 h-4 animate-spin"
                        icon={faSpinner}
                      />
                      Submitting...
                    </span>
                  ) : (
                    "Submit Feedback"
                  )}
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default EventFeedbackForm
